import { flagToBool, toFlag } from 'pano-utils';
import { format, fromUnixTime } from 'date-fns';
import { type SceneCompareConfig, type SceneData, SceneType } from '@types';
import { Collapse } from '@components/Collapse';
import FeatureToggle from '@components/FeatureToggle';
import Input from '@components/Input';
import TextLink from '@components/TextLink';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconExit from '@components/icons/IconExit';
import useInputHandlers from '@hooks/useInputHandelrs';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import { $sceneMapApi } from '@stores/dataStores';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';

interface Props {
  scene: SceneData;
  onQuitClick: () => void;
}

const configDefault: Omit<SceneCompareConfig, 'scenes'> = {
  originTitle: '',
  showModeOption: 1,
  showScenesList: 0,
};

export function CompareSettings({ scene, onQuitClick }: Props) {
  const {
    originTitle = configDefault.originTitle,
    showModeOption = configDefault.showModeOption,
    showScenesList = configDefault.showScenesList,
  } = scene.compareConfig ?? {};

  function updateValue<K extends keyof SceneCompareConfig>(
    k: K,
    val: SceneCompareConfig[K]
  ) {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.compareConfig ??= {} as SceneCompareConfig;
        draft.compareConfig[k] = val;
      },
    });
  }

  const titleHandler = useInputHandlers((val) => {
    updateValue('originTitle', val);
  });

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      allowedTypes: [SceneType.pano],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const item = draft.get(_id);
        if (!item) return;
        item.compareConfig ??= {} as SceneCompareConfig;
        item.compareConfig.showModeOption = showModeOption;
        item.compareConfig.showScenesList = showScenesList;
      });
    });
    sendSuccess('对比功能设置已应用到所选场景');
  };

  return (
    <>
      <SidebarHeader
        hideCollapse
        title={
          <div className="flex">
            <Title>场景对比</Title>
            <Tip className="ml-10 self-end text-base">对比设置</Tip>
          </div>
        }
        btns={
          <TipWrapper text="退出操作" placement="bottom" size="sm">
            <IconExit className="focus:outline-none" onClick={onQuitClick} />
          </TipWrapper>
        }
      />
      <Collapse title="对比设置">
        <FeatureToggle
          title="对比模式切换栏"
          isOn={flagToBool(showModeOption)}
          onToggle={(bool) => {
            updateValue('showModeOption', toFlag(bool));
          }}
        />
        <Tip className="mt-10">
          说明：开启后在进入场景对比时将显示场景对比模式切换栏，支持切换对比方式
        </Tip>
        <FeatureToggle
          className="mt-20"
          title="场景选择栏"
          isOn={flagToBool(showScenesList)}
          onToggle={(bool) => {
            updateValue('showScenesList', toFlag(bool));
          }}
        />
        <Tip className="mt-10">
          说明：开启后在进入场景对比时将显示场景选择栏，支持切换其他场景对比
        </Tip>
        <SidebarApplyTo
          title="把对比功能设置应用到..."
          className="mt-20"
          onClick={handleApplyTo}
        />
      </Collapse>
      <Collapse title="标题设置">
        <div className="flex items-center">
          <div>原始场景标题</div>
          <TextLink
            className="ml-auto"
            onClick={() => {
              const dateText = format(
                fromUnixTime(scene.createDate),
                'yyyy-MM-dd'
              );
              updateValue('originTitle', dateText);
            }}
          >
            填入场景创建日期
          </TextLink>
        </div>
        <Input
          scheme="dark"
          className="mt-10 h-40"
          placeholder="为空时默认为原始场景名称"
          maxLength={50}
          value={originTitle}
          {...titleHandler}
        />
        <Tip className="mt-10">
          提示：对于工程记录/巡检等应用场景，可将对比场景名称设置为施工/巡检日期以便场景对比时可以更直观查看不同时间点的工程进度/巡检情况
        </Tip>
      </Collapse>
    </>
  );
}
